import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../App";
import { usePlatformMode } from "../context/PlatformModeContext";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { clearAdminReturnPath, storeAdminReturnPath } from "../utils/adminReturnIntent";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function getNavIconPalette(tone) {
  if (tone === "emerald") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (tone === "amber") return "bg-amber-50 text-amber-700 border-amber-200";
  if (tone === "violet") return "bg-violet-50 text-violet-700 border-violet-200";
  if (tone === "rose") return "bg-rose-50 text-rose-700 border-rose-200";
  if (tone === "sky") return "bg-sky-50 text-sky-700 border-sky-200";
  if (tone === "indigo") return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (tone === "teal") return "bg-teal-50 text-teal-800 border-teal-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

/** Contacts list is one page; RE vs construction is a filter (?tab=). */
function adminNavItemActive(location, it) {
  if (typeof it.isActive === "function") return it.isActive(location);
  const rawTo = it.to || "";
  const [path, query] = rawTo.split("?");
  if (location.pathname !== path) return false;
  if (!query) return true;
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(location.search);
  return want.toString() === have.toString();
}

function WorkspaceIcon({ glyph, tone, active }) {
  return (
    <span
      aria-hidden="true"
      className={classNames(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border text-[11px] font-semibold transition-all",
        active ? "border-white/60 bg-white/15 text-white shadow-sm" : getNavIconPalette(tone)
      )}
    >
      {glyph}
    </span>
  );
}

async function ensureUserProfile(u) {
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    const email = u.email || "";
    await setDoc(ref, {
      email,
      role: "client",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { email, role: "client" };
  }

  return snap.data();
}

function AdminLayout() {
  const navigate = useNavigate();
  const { mode, setMode } = usePlatformMode();
  const location = useLocation();

  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("adminRailCollapsed") === "1";
    } catch {
      return false;
    }
  });

  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [error, setError] = useState("");

  const viewerIsAdmin = useMemo(
    () => (viewerProfile?.role || "").toString().toLowerCase() === "admin",
    [viewerProfile]
  );

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      setLoading(true);
      setError("");
      setViewer(null);
      setViewerProfile(null);

      if (!u) {
        storeAdminReturnPath(`${location.pathname}${location.search}${location.hash}`);
        navigate("/client-portal", { replace: true });
        return;
      }

      setViewer(u);

      try {
        const prof = await ensureUserProfile(u);
        setViewerProfile(prof);

        if ((prof?.role || "").toString().toLowerCase() !== "admin") {
          storeAdminReturnPath(`${location.pathname}${location.search}${location.hash}`);
          setError("Access denied — admin only.");
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (e) {
        console.error("AdminLayout load error:", e);
        setError(e?.message || "Failed to load admin profile.");
        setLoading(false);
      }
    });

    return () => unsub();
  }, [navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const path = (location?.pathname || "").toLowerCase();
    const isRealEstateRoute = path.startsWith("/admin/real-estate");
    const isConstructionRoute = path.startsWith("/admin") && !isRealEstateRoute;

    if (isRealEstateRoute && mode !== "realEstate") setMode("realEstate");
    if (isConstructionRoute && mode !== "construction") setMode("construction");
  }, [location.pathname, mode, setMode]);

  const navSections = [
    {
      key: "crm-and-tasks",
      title: "CRM and Tasks",
      items: [
        {
          to: "/admin/clients",
          label: "Contacts",
          glyph: "CR",
          tone: "violet",
          isActive: (loc) => loc.pathname === "/admin/clients" || loc.pathname.startsWith("/admin/users/"),
        },
        { to: "/admin/tasks", label: "Tasks", glyph: "TK", tone: "amber" },
        {
          to: "/admin/notifications",
          label: "Notifications",
          glyph: "OX",
          tone: "violet",
          isActive: (loc) => loc.pathname === "/admin/notifications",
        },
        {
          to: "/admin/password-reset-audit",
          label: "Password Reset Audit",
          glyph: "PR",
          tone: "sky",
          isActive: (loc) => loc.pathname === "/admin/password-reset-audit",
        },
      ],
    },
    {
      key: "construction",
      title: "Construction",
      items: [
        { to: "/admin/dashboard", label: "Dashboard", glyph: "DB", tone: "indigo" },
        { to: "/admin/projects", label: "Projects", glyph: "PR", tone: "amber" },
        { to: "/admin/jurisdictions", label: "Jurisdiction Library", glyph: "JR", tone: "sky" },
      ],
    },
    {
      key: "real-estate",
      title: "Real Estate",
      items: [
        { to: "/admin/real-estate", label: "Overview", glyph: "OV", tone: "emerald" },
        {
          to: "/admin/real-estate/client-properties",
          label: "Client properties",
          glyph: "CP",
          tone: "indigo",
          isActive: (loc) => loc.pathname.startsWith("/admin/real-estate/client-properties"),
        },
        { to: "/admin/real-estate/listings", label: "Listings", glyph: "LS", tone: "amber" },
        { to: "/admin/real-estate/transactions", label: "Transactions", glyph: "TR", tone: "slate" },
        { to: "/admin/real-estate/documents", label: "Documents", glyph: "DC", tone: "sky" },
        { to: "/admin/real-estate/reports", label: "Reports", glyph: "RP", tone: "rose" },
        { to: "/admin/real-estate/condos", label: "Condos", glyph: "CD", tone: "teal" },
        { to: "/admin/real-estate/condo-client-access", label: "Condo clients", glyph: "CC", tone: "cyan" },
        { to: "/admin/real-estate/cma-requests", label: "CMA requests", glyph: "CM", tone: "amber" },
        { to: "/admin/real-estate/client-intent", label: "Client intent", glyph: "IN", tone: "teal" },
      ],
    },
    {
      key: "website-admin",
      title: "Website Admin",
      items: [
        { to: "/admin/public-projects", label: "Public Projects", glyph: "PP", tone: "amber" },
        { to: "/admin/portfolio", label: "Portfolio", glyph: "PF", tone: "emerald" },
        { to: "/admin/health", label: "Data health", glyph: "DH", tone: "sky" },
        { to: "/admin/settings", label: "Settings", glyph: "ST", tone: "violet" },
      ],
    },
  ];


  const toggleRail = () => {
    setRailCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem("adminRailCollapsed", next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  if (loading) {
    return <div className="container mx-auto p-4 text-center">Loading admin...</div>;
  }

  if (viewer && viewerProfile && !viewerIsAdmin) {
    return (
      <div className="container mx-auto p-4">
        <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
          <p className="mb-4 text-slate-900">This link requires admin access.</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              onClick={async () => {
                try {
                  await signOut(auth);
                } catch (e) {
                  console.error(e);
                }
                navigate("/client-portal");
              }}
            >
              Switch to admin login
            </button>
            <button
              type="button"
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              onClick={() => {
                clearAdminReturnPath();
                navigate("/client-portal");
              }}
            >
              Go to client portal
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !viewer || !viewerIsAdmin) {
    return (
      <div className="container mx-auto p-4">
        <div className="border p-4 rounded">
          <p className="text-red-600 mb-3">{error || "Access denied."}</p>
          <div className="flex gap-2">
            <button className="border px-3 py-2" onClick={() => navigate("/client-portal")}>
              Go to Client Portal
            </button>
            <button className="border px-3 py-2" onClick={() => navigate("/")}>
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 border-b border-slate-200 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <Link to="/admin/dashboard" className="text-xl font-bold text-slate-900 hover:opacity-80">
              Mark Beattie Admin
            </Link>
            <span className="truncate text-sm text-slate-600" title={viewer.email || undefined}>
              {viewer.email}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Link
              to="/"
              className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Marketing website
            </Link>
            <button
              type="button"
              className="inline-flex items-center rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
              onClick={async () => {
                try {
                  await signOut(auth);
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Sign out
            </button>
          </div>
        </div>
        <div className="truncate text-xs text-slate-400" title={location.pathname}>
          {location.pathname}
        </div>
      </div>

      <div
        className={classNames(
          "grid grid-cols-1 gap-4",
          railCollapsed ? "md:grid-cols-[84px_1fr]" : "md:grid-cols-[280px_1fr]"
        )}
      >
        <aside className={classNames("rounded-2xl border border-slate-200 bg-white shadow-sm", railCollapsed ? "p-2" : "p-3")}>
          <div className={classNames("flex items-center", railCollapsed ? "justify-center" : "justify-between")}>
            {railCollapsed ? null : (
              <div>
                <div className="text-sm font-semibold text-slate-700">Workspaces</div>
                <div className="text-xs text-slate-500">Projects run the work. Jurisdictions manage the permit intelligence.</div>
              </div>
            )}
            <button
              type="button"
              className={classNames(
                "rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:bg-slate-100",
                railCollapsed ? "flex h-10 w-10 items-center justify-center" : "px-2.5 py-2"
              )}
              onClick={toggleRail}
              title={railCollapsed ? "Expand navigation" : "Collapse navigation"}
              aria-label={railCollapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {railCollapsed ? "»" : "«"}
            </button>
          </div>

          <nav className="mt-4 flex flex-col gap-4">
            {navSections.map((section) => (
              <div key={section.key} className="space-y-1.5">
                {railCollapsed ? null : (
                  <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                    {section.title}
                  </div>
                )}

                {section.items.map((it) => (
                  <NavLink
                    key={`${section.key}-${it.to}`}
                    to={it.to}
                    className={() =>
                      classNames(
                        "group block rounded-2xl border transition-all",
                        railCollapsed ? "px-2 py-2" : "px-2.5 py-2",
                        adminNavItemActive(location, it)
                          ? "border-accent bg-accent text-white shadow-sm"
                          : "border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50"
                      )
                    }
                    title={railCollapsed ? `${section.title} — ${it.label}` : undefined}
                  >
                    {() => (
                      <span className={classNames("flex items-center", railCollapsed ? "justify-center" : "gap-3")}>
                        <WorkspaceIcon
                          glyph={it.glyph}
                          tone={it.tone}
                          active={adminNavItemActive(location, it)}
                        />
                        {railCollapsed ? null : (
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{it.label}</span>
                          </span>
                        )}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          {railCollapsed ? null : (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs leading-5 text-slate-600">
              <span className="font-medium text-slate-700">Contacts</span> is the single list for everyone — use the Service buttons on that page (Real Estate / Construction / All).{" "}
              <span className="font-medium text-slate-700">Tasks</span> is the shared action list.
              Construction and Real Estate sections hold tools for each line of work.{" "}
              <span className="font-medium text-slate-700">Data health</span> checks Firestore data quality.
            </div>
          )}
        </aside>

        <section className="border rounded p-4 bg-white">
          <Outlet />
        </section>
      </div>
    </div>
  );
}

export default AdminLayout;
